/**
 * The builder helper's conversation state — local to the launcher, nothing
 * persisted. Each turn posts the whole conversation plus the CURRENT draft
 * context to orchestration's builder-helper endpoint, which now BUILDS as well
 * as answers: a turn resolves to prose, a question card, or a workflow
 * proposal. The proposal's "Create draft" commits via a separate endpoint
 * (creating any pending DM channels, then the disabled composed workflow).
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import type { ChatChoiceAnswer } from '@servicecompressionllc/sc-design-system';
import { apiRequest } from '../../lib/apiClient.js';
import { agentBaseUrl } from '../../lib/agentApi.js';
import type { BuilderDefinition } from '../../lib/types.js';

export interface AskQuestionPayload {
  question: string;
  options: { value: string; label: string; description?: string }[];
  allowOther?: boolean;
  otherPlaceholder?: string;
}

export interface PendingChannel {
  ref: string;
  targetUserId: string;
  label: string;
}

export interface ProposalPayload {
  name: string;
  trigger: string;
  definition: BuilderDefinition;
  summary: string;
  warnings?: string[];
  pendingChannels?: PendingChannel[];
  /** Server-run completeness check — what still blocks enabling. */
  issues: string[];
}

/** Every message carries `content` (the text the model's history needs); the
 *  assistant's structured turns add a `kind` + payload the panel renders. */
export type HelperMessage =
  | {
      id: string;
      role: 'user' | 'assistant';
      content: string;
      kind?: undefined;
      /** What the MODEL sees for this turn (carries an option's id); `content`
       *  is what the transcript shows. Falls back to `content` when unset. */
      apiContent?: string;
    }
  | {
      id: string;
      role: 'assistant';
      content: string;
      kind: 'question';
      question: AskQuestionPayload;
      answered?: ChatChoiceAnswer;
    }
  | {
      id: string;
      role: 'assistant';
      content: string;
      kind: 'proposal';
      proposal: ProposalPayload;
      status: 'pending' | 'approved' | 'declined';
    };

type HelperResponse =
  | { kind: 'message'; answer: string }
  | { kind: 'question'; question: AskQuestionPayload }
  | { kind: 'proposal'; proposal: ProposalPayload };

// Lenient envelope validation at the boundary (CLAUDE.md: Zod at every
// boundary). `definition` stays `unknown` — the server forwards a raw,
// possibly-invalid definition when `issues` is populated; the mapper/render
// guard against that. `.passthrough()` so benign server additions don't reject.
const helperResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('message'), answer: z.string() }).passthrough(),
  z
    .object({
      kind: z.literal('question'),
      question: z
        .object({
          question: z.string(),
          options: z.array(z.object({ value: z.string(), label: z.string() }).passthrough()),
        })
        .passthrough(),
    })
    .passthrough(),
  z
    .object({
      kind: z.literal('proposal'),
      proposal: z
        .object({
          name: z.string(),
          trigger: z.string(),
          summary: z.string(),
          issues: z.array(z.string()),
        })
        .passthrough(),
    })
    .passthrough(),
]);

export interface HelperContext {
  trigger?: string;
  definition?: BuilderDefinition;
}

export const useBuilderHelperLogic = (
  context: HelperContext,
  /** Given only when it is safe to show a half-built workflow on screen —
   * the page decides that, not this hook. */
  onDraftPreview?: (draft: {
    name?: string;
    trigger?: string;
    definition?: unknown;
    pendingChannels?: { ref: string; targetUserId: string }[];
  }) => void,
  /** Told when the helper starts and stops working, so the builder behind the
   * panel can show that something is coming. Driven from the handlers rather
   * than an effect watching `isPending`. */
  onBusyChange?: (busy: boolean) => void,
) => {
  const [messages, setMessages] = useState<HelperMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [committingId, setCommittingId] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: (all: HelperMessage[]) =>
      apiRequest<HelperResponse>(
        '/api/builder-helper',
        {
          method: 'POST',
          baseUrl: agentBaseUrl,
          body: {
            // The endpoint caps the conversation at 20 turns; send the tail.
            // Use apiContent (carries an option's id) when present, else content.
            messages: all.slice(-20).map((m) => ({
              role: m.role,
              content: (m.kind === undefined ? m.apiContent : undefined) ?? m.content,
            })),
            context: {
              ...(context.trigger ? { trigger: context.trigger } : {}),
              ...(context.definition ? { definition: context.definition } : {}),
            },
          },
        },
        helperResponseSchema,
      ),
    onSuccess: (r) => {
      onBusyChange?.(false);
      setMessages((prev) => {
        const id = `a-${String(prev.length)}`;
        if (r.kind === 'question') {
          // Show what it has settled so far — the workflow assembles behind
          // the panel instead of appearing all at once at the end.
          const partial = (
            r.question as { draft?: { name?: string; trigger?: string; definition?: unknown } }
          ).draft;
          if (partial && onDraftPreview) onDraftPreview(partial);
          return [
            ...prev,
            {
              id,
              role: 'assistant',
              content: r.question.question,
              kind: 'question',
              question: r.question,
            },
          ];
        }
        if (r.kind === 'proposal') {
          // The finished workflow previews too. Streaming the half-built one
          // and then withholding the finished one is the worst of both: the
          // page had been filling in, then stops at "nothing happens" while
          // the card claims a step. Confirming is still what commits it.
          if (onDraftPreview)
            onDraftPreview({
              name: r.proposal.name,
              trigger: r.proposal.trigger,
              definition: r.proposal.definition,
              pendingChannels: r.proposal.pendingChannels,
            });
          return [
            ...prev,
            {
              id,
              role: 'assistant',
              content: r.proposal.summary,
              kind: 'proposal',
              proposal: r.proposal,
              status: 'pending',
            },
          ];
        }
        return [...prev, { id, role: 'assistant', content: r.answer }];
      });
    },
    // A failed turn must clear the builder's waiting row too, or the page
    // keeps promising a step that is never coming.
    onError: () => onBusyChange?.(false),
  });

  const commit = useMutation({
    mutationFn: (proposal: ProposalPayload) =>
      apiRequest<{ id: string }>('/api/builder-helper/commit', {
        method: 'POST',
        baseUrl: agentBaseUrl,
        body: {
          name: proposal.name,
          trigger: proposal.trigger,
          definition: proposal.definition,
          ...(proposal.pendingChannels ? { pendingChannels: proposal.pendingChannels } : {}),
        },
      }),
  });

  const send = () => {
    const content = draft.trim();
    if (content === '' || ask.isPending) return;
    const next: HelperMessage[] = [
      ...messages,
      { id: `u-${String(messages.length)}`, role: 'user', content },
    ];
    setMessages(next);
    setDraft('');
    onBusyChange?.(true);
    ask.mutate(next);
  };

  /** Answer a question card: lock it, echo the choice as a user turn, continue. */
  const answerQuestion = (id: string, answer: ChatChoiceAnswer) => {
    if (ask.isPending) return;
    const q = messages.find((m) => m.id === id);
    const label =
      answer.kind === 'text'
        ? answer.text
        : (q?.kind === 'question' &&
            q.question.options.find((o) => o.value === answer.value)?.label) ||
          answer.value;
    // The transcript shows the label; the MODEL must get the id so it can tell
    // look-alike options apart (two "Travis"es differ only by value).
    const apiContent =
      answer.kind === 'text' ? answer.text : `Chose "${label}" (id: ${answer.value})`;
    const marked = messages.map((m) =>
      m.id === id && m.kind === 'question' ? { ...m, answered: answer } : m,
    );
    const next: HelperMessage[] = [
      ...marked,
      { id: `u-${String(marked.length)}`, role: 'user', content: label, apiContent },
    ];
    setMessages(next);
    onBusyChange?.(true);
    ask.mutate(next);
  };

  /** Create the drafted workflow (disabled), plus any pending DM channels. */
  /**
   * Editor path: hand the revised definition to the page instead of creating
   * anything. The person's click is still the write — the editor's autosave
   * persists it, and they can keep editing or undo by hand afterwards.
   */
  const applyProposal = (
    id: string,
    apply: (proposal: {
      name?: string;
      trigger?: string;
      definition: BuilderDefinition;
      pendingChannels?: { ref: string; targetUserId: string }[];
    }) => void,
  ) => {
    const m = messages.find((x) => x.id === id);
    if (!m || m.kind !== 'proposal') return;
    const definition = m.proposal.definition;
    if (typeof definition !== 'object' || definition === null) return;
    apply({
      name: m.proposal.name,
      trigger: m.proposal.trigger,
      definition: definition as BuilderDefinition,
      pendingChannels: m.proposal.pendingChannels,
    });
    setMessages((prev) => [
      ...prev.map((x) =>
        x.id === id && x.kind === 'proposal' ? { ...x, status: 'approved' as const } : x,
      ),
      {
        id: `a-a-${id}`,
        role: 'assistant' as const,
        content:
          'Saved it as a draft — it stays turned off until you switch it on. Have a read through, and tell me anything I got wrong.',
      },
    ]);
  };

  const commitProposal = (id: string) => {
    const m = messages.find((x) => x.id === id);
    if (!m || m.kind !== 'proposal' || commit.isPending) return;
    const { proposal } = m;
    setCommittingId(id);
    commit.mutate(proposal, {
      onSuccess: () => {
        setMessages((prev) => [
          ...prev.map((x) =>
            x.id === id && x.kind === 'proposal' ? { ...x, status: 'approved' as const } : x,
          ),
          {
            id: `a-c-${id}`,
            role: 'assistant',
            content: `Created the draft “${proposal.name}”. It’s saved turned off — open it to review and switch it on.`,
          },
        ]);
      },
      onSettled: () => setCommittingId(null),
    });
  };

  const dismissProposal = (id: string) => {
    setMessages((prev) =>
      prev.map((x) =>
        x.id === id && x.kind === 'proposal' ? { ...x, status: 'declined' as const } : x,
      ),
    );
  };

  return {
    messages,
    draft,
    setDraft,
    send,
    thinking: ask.isPending,
    error: ask.error,
    answerQuestion,
    commitProposal,
    applyProposal,
    dismissProposal,
    committingId,
    commitError: commit.error,
  };
};
